import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, Palette } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { usePageTitle } from "@/lib/title";
import { applyBrandColor, generateBrandShades, BRAND_SHADES, type BrandingSettings } from "@/lib/branding";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FullPageSpinner } from "@/components/ui/Spinner";

export function SettingsPage() {
  usePageTitle("Settings");
  const queryClient = useQueryClient();
  const { notify } = useToast();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<BrandingSettings>("/api/v1.0/settings"),
  });

  const [appName, setAppName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#2563eb");
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!settings) return;
    setAppName(settings.app_name);
    setPrimaryColor(settings.primary_color);
    setLogoUrl(settings.logo_url);
  }, [settings]);

  const { mutate: uploadLogo, isPending: uploading } = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.postForm<{ url: string }>("/api/v1.0/uploads", form);
    },
    onSuccess: (res) => setLogoUrl(res.url),
    onError: (err) => notify(err instanceof ApiError ? err.message : "Failed to upload logo", "error"),
  });

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: () =>
      api.patch<BrandingSettings>("/api/v1.0/settings", {
        app_name: appName,
        primary_color: primaryColor,
        logo_url: logoUrl,
      }),
    onSuccess: (updated) => {
      notify("Branding updated");
      queryClient.setQueryData(["settings"], updated);
    },
    onError: (err) => {
      notify(err instanceof ApiError ? err.message : "Failed to save settings", "error");
      // Revert the live preview to the last saved color.
      if (settings) applyBrandColor(settings.primary_color);
    },
  });

  const onColorChange = (value: string) => {
    setPrimaryColor(value);
    if (/^#[0-9a-fA-F]{6}$/.test(value)) applyBrandColor(value);
  };

  if (isLoading) return <FullPageSpinner />;

  const dirty =
    !!settings && (appName !== settings.app_name || primaryColor !== settings.primary_color || logoUrl !== settings.logo_url);
  const shades = generateBrandShades(/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : "#2563eb");

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Customize how the platform looks for everyone.</p>

      <Card>
        <CardBody className="flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-brand-600" />
            <h3 className="text-sm font-semibold text-slate-900">Branding</h3>
          </div>

          <Input
            label="Platform name"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            placeholder="ZKI LMS"
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">Logo</label>
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo preview" className="h-full w-full object-contain" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-slate-300" />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="w-fit cursor-pointer text-sm font-medium text-brand-600 hover:underline">
                  {uploading ? "Uploading..." : "Upload image"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadLogo(file);
                    }}
                  />
                </label>
                <p className="text-xs text-slate-400">Square image works best. PNG or SVG with a transparent background.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">Primary color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : "#2563eb"}
                onChange={(e) => onColorChange(e.target.value)}
                className="h-10 w-14 shrink-0 cursor-pointer rounded-md border border-slate-300 p-0.5"
              />
              <Input
                value={primaryColor}
                onChange={(e) => onColorChange(e.target.value)}
                placeholder="#2563eb"
                className="w-32 font-mono"
              />
              <div className="flex gap-1">
                {BRAND_SHADES.map((shade) => (
                  <div
                    key={shade}
                    title={`brand-${shade}`}
                    className="h-6 w-6 rounded"
                    style={{ backgroundColor: shades[shade] }}
                  />
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-400">Used for buttons, links, and highlights across the app.</p>
          </div>

          <div className="flex justify-end border-t border-slate-100 pt-4">
            <Button loading={saving} disabled={!dirty} onClick={() => save()}>
              Save changes
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
